import { Popover as HSPopover, PopoverButton as HSPopoverButton, PopoverPanel as HSPopoverPanel } from '@headlessui/react';
import classNames from 'classnames';
import React, { memo, SVGProps } from 'react';

export interface HeaderDropdownButtonProps {
  /** Heroicon or SVG component for the button */
  icon: React.ForwardRefExoticComponent<Omit<SVGProps<SVGSVGElement>, 'ref'> & { title?: string; titleId?: string } & React.RefAttributes<SVGSVGElement>>;
  /** Title/tooltip shown in the dropdown panel header */
  title: React.ReactNode;
  /** Optional element rendered in the right side of the dropdown header (e.g. an icon action) */
  headerAction?: React.ReactNode;
  /** Dropdown panel body */
  children: React.ReactNode | ((opts: { close: () => void }) => React.ReactNode);
  /** Optional footer rendered at the bottom of the panel */
  footer?: React.ReactNode;
  /** Show a green spinner ring overlay around the icon */
  inProgress?: boolean;
  /** Show a green notification dot in the top-right corner */
  showDot?: boolean;
  /** Show a numeric badge in the top-right corner (takes precedence over showDot) */
  badgeCount?: number;
  /** Override the default panel width classes if needed */
  panelWidthClassName?: string;
  /** Where the panel opens relative to the button */
  anchor?: `${'top' | 'right' | 'bottom' | 'left'} ${'start' | 'end'}`;
}

const HeaderDropdownButton_: React.FC<HeaderDropdownButtonProps> = ({ icon: Icon, title, headerAction, children, footer, inProgress, showDot, badgeCount, panelWidthClassName = 'w-max min-w-[280px] max-w-[520px]', anchor = 'bottom end' }) => {
  const hasBadge = typeof badgeCount === 'number' && badgeCount > 0;

  return (
    <HSPopover className="relative">
      <HSPopoverButton as="button" className="relative flex items-center justify-center w-11 h-11 rounded-full border-2 border-black text-black hover:bg-gray-200 dark:text-white dark:border-white dark:hover:bg-gray-800 transition-colors outline-none cursor-pointer">
        <Icon className="w-5 h-5" />
        {inProgress && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="w-11 h-11 rounded-full border-2 border-green-500 border-t-transparent animate-spin" />
          </span>
        )}
        {hasBadge ? (
          <span className={classNames('absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-green-500 text-black border-2 border-white dark:border-[#121212] font-medium', badgeCount >= 10 ? 'min-w-[18px] h-[18px] text-[10px] px-1' : 'w-[18px] h-[18px] text-[10px]')}>
            {badgeCount >= 10 ? '9+' : badgeCount}
          </span>
        ) : (
          showDot && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white dark:border-[#121212]" />
        )}
      </HSPopoverButton>
      <HSPopoverPanel anchor={anchor} className={classNames('z-50 !bg-white dark:!bg-[#242424] flex flex-col shadow-lg ring-1 ring-gray-400 dark:text-white rounded-md [--anchor-gap:10px]', panelWidthClassName)}>
        {({ close }) => (
          <>
            <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm font-semibold border-b border-gray-200 dark:border-gray-600">
              <span>{title}</span>
              {headerAction}
            </div>
            <div className="flex flex-col max-h-96 overflow-y-auto">{typeof children === 'function' ? children({ close }) : children}</div>
            {footer && <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-600">{footer}</div>}
          </>
        )}
      </HSPopoverPanel>
    </HSPopover>
  );
};

export const HeaderDropdownButton = memo(HeaderDropdownButton_);
